// Derives an operator-facing "what is wrong right now" list from the
// /dashboard/stats payload.
//
// The operations dashboard used to render every metric at equal weight, so a
// genuinely bad state (most of the fleet offline, an alert that never reached a
// caregiver) looked exactly like the dozens of healthy zeros around it. This
// module turns the same payload into a ranked, plain-English list with a
// deep-link per item, so the top of the page answers "is anything wrong, and
// where do I go" before it answers "what are the numbers".
//
// Pure functions only — no React, no network — so the thresholds are unit
// testable without a DOM.

// Tunables, exported so tests assert against the same numbers the UI uses.
export const THRESHOLDS = {
  // Fraction of the live fleet that must be offline before it is critical
  // rather than a warning.
  offlineFleetFraction: 0.5,
  // Mean uptime below this (hours) means devices are rebooting or reconnecting
  // rather than sitting up healthy.
  lowUptimeHours: 1,
  // Weakest acceptable RSSI before the link is worth flagging.
  weakSignalDbm: -80,
  // Don't draw uptime/signal conclusions from a handful of telemetry rows.
  minTelemetrySamples: 5,
};

const SEVERITY_RANK = { critical: 0, warning: 1, info: 2 };

// MySQL SUM()/COUNT() can arrive as strings; coerce defensively.
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const plural = (n, word, suffix = 's') => `${word}${n === 1 ? '' : suffix}`;

/** Formats an hour count as minutes when it is under an hour. */
export function formatUptime(hours) {
  if (hours == null || !Number.isFinite(Number(hours))) return '--';
  const h = Number(hours);
  if (h < 1) return `${Math.round(h * 60)} min`;
  return `${h}h`;
}

/** Plain-language band for a Wi-Fi RSSI reading. */
export function signalQuality(dbm) {
  if (dbm == null || !Number.isFinite(Number(dbm))) return null;
  const d = Number(dbm);
  if (d >= -55) return { label: 'Excellent', tone: 'good' };
  if (d >= -67) return { label: 'Good', tone: 'good' };
  if (d >= -75) return { label: 'Fair', tone: 'ok' };
  if (d >= -85) return { label: 'Weak', tone: 'warn' };
  return { label: 'Poor', tone: 'bad' };
}

/** Parses "4.1.10" / "v4.1.10" into [4,1,10]; null when unparseable. */
export function parseVersion(v) {
  if (typeof v !== 'string') return null;
  const m = v.trim().match(/^v?(\d+)\.(\d+)\.(\d+)/);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/**
 * Numeric-segment version compare (so 4.1.10 > 4.1.9, which a string sort gets
 * backwards). Unparseable versions — e.g. the endpoint's 'unknown' bucket —
 * sort below every real version.
 */
export function compareVersions(a, b) {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa && !pb) return 0;
  if (!pa) return -1;
  if (!pb) return 1;
  for (let i = 0; i < 3; i += 1) {
    if (pa[i] !== pb[i]) return pa[i] < pb[i] ? -1 : 1;
  }
  return 0;
}

/**
 * Annotates the firmware distribution with which version leads the fleet and
 * how many devices trail it. The highest version present is treated as the
 * de-facto current build — the stats endpoint does not expose an assigned
 * fleet target, so this deliberately reports "behind the newest thing running"
 * rather than claiming to know the intended target.
 */
export function firmwareSummary(distribution) {
  const rows = (distribution || []).map((r) => ({
    version: r.version,
    board: r.board,
    count: num(r.count),
  }));

  let latest = null;
  rows.forEach((r) => {
    if (parseVersion(r.version) && (latest === null || compareVersions(r.version, latest) > 0)) {
      latest = r.version;
    }
  });

  const annotated = rows
    .map((r) => ({
      ...r,
      isLatest: latest != null && compareVersions(r.version, latest) === 0,
      behind: latest != null && compareVersions(r.version, latest) < 0,
    }))
    .sort((a, b) => compareVersions(b.version, a.version) || b.count - a.count);

  const lagging = annotated.filter((r) => r.behind);
  return {
    latest,
    rows: annotated,
    laggingCount: lagging.reduce((sum, r) => sum + r.count, 0),
    // De-duplicated so two board models on the same old version read once.
    laggingVersions: [...new Set(lagging.map((r) => r.version))],
  };
}

/** Delivery health over the notification window, or null when nothing was sent. */
export function deliverySummary(notifications) {
  const n = notifications || {};
  const delivered = num(n.delivered);
  const failed = num(n.failed);
  const awaiting = num(n.sent);
  const settled = delivered + failed;
  return {
    delivered,
    failed,
    awaiting,
    total: delivered + failed + awaiting,
    // Rate over *settled* attempts only — counting not-yet-confirmed sends as
    // failures would make a healthy queue look broken.
    rate: settled > 0 ? Math.round((delivered / settled) * 100) : null,
  };
}

/**
 * Builds the ranked attention list. Returns [] for a healthy fleet, which the
 * UI renders as an explicit all-clear rather than an empty region.
 */
export function buildTriage(stats) {
  if (!stats) return [];

  const devices = stats.devices || {};
  const alerts = stats.alerts || {};
  const checkins = stats.checkins || {};
  const ota = stats.ota || {};
  const notifications = stats.notifications || {};
  const telemetry = stats.telemetry || {};

  const items = [];

  // --- Critical: someone needs a human right now -------------------------
  const criticalPending = num(alerts.criticalPending);
  if (criticalPending > 0) {
    items.push({
      id: 'alerts-critical',
      severity: 'critical',
      title: `${criticalPending} critical ${plural(criticalPending, 'alert')} awaiting response`,
      detail: 'Raised and still unacknowledged by anyone.',
      to: '/alerts',
      cta: 'Triage alerts',
    });
  }

  const missed = num(checkins.missedToday);
  if (missed > 0) {
    items.push({
      id: 'checkins-missed',
      severity: 'critical',
      title: `${missed} missed ${plural(missed, 'check-in')} today`,
      detail: 'A scheduled window closed with no button press.',
      to: '/monitor',
      cta: 'Open live monitor',
    });
  }

  // A failed notification is a caregiver who was never told — treat it as
  // seriously as the alert that triggered it.
  const failedNotifs = num(notifications.failed);
  if (failedNotifs > 0) {
    const channels = Object.entries(notifications.byChannel || {})
      .filter(([, c]) => num(c.failed) > 0)
      .map(([channel, c]) => `${channel} ${num(c.failed)}`);
    items.push({
      id: 'notifications-failed',
      severity: 'critical',
      title: `${failedNotifs} ${plural(failedNotifs, 'notification')} failed to deliver`,
      detail: channels.length
        ? `Last 7 days — failing on ${channels.join(', ')}.`
        : 'Last 7 days. A caregiver may never have been told.',
      to: '/notifications',
      cta: 'Review delivery log',
    });
  }

  // --- Fleet reachability -------------------------------------------------
  const offline = num(devices.offline);
  const live = Math.max(0, num(devices.total) - num(devices.decommissioned));
  if (offline > 0) {
    const fraction = live > 0 ? offline / live : 1;
    items.push({
      id: 'devices-offline',
      severity: fraction >= THRESHOLDS.offlineFleetFraction ? 'critical' : 'warning',
      title: live > 0
        ? `${offline} of ${live} ${plural(live, 'device')} offline`
        : `${offline} ${plural(offline, 'device')} offline`,
      detail: 'No heartbeat in 15 minutes — an offline device cannot raise an alert.',
      to: '/devices',
      cta: 'Open device list',
    });
  }

  // --- Warnings -----------------------------------------------------------
  const otherPending = Math.max(0, num(alerts.pending) - criticalPending);
  if (otherPending > 0) {
    items.push({
      id: 'alerts-pending',
      severity: 'warning',
      title: `${otherPending} non-critical ${plural(otherPending, 'alert')} pending`,
      detail: 'Open and unacknowledged.',
      to: '/alerts',
      cta: 'Review alerts',
    });
  }

  const otaQueued = num(ota.pending) + num(ota.notified);
  const otaMoving = num(ota.downloading) + num(ota.applying);
  if (otaQueued > 0 && otaMoving === 0 && num(ota.success) === 0) {
    items.push({
      id: 'ota-stalled',
      severity: 'warning',
      title: `${otaQueued} OTA ${plural(otaQueued, 'rollout')} assigned but not started`,
      detail: 'No device has downloaded or applied the target build — check they are online.',
      to: '/firmware',
      cta: 'Open firmware & OTA',
    });
  }

  const otaFailed = num(ota.failed);
  if (otaFailed > 0) {
    items.push({
      id: 'ota-failed',
      severity: 'warning',
      title: `${otaFailed} OTA ${plural(otaFailed, 'rollout')} failed`,
      detail: 'Devices stayed on their previous build.',
      to: '/firmware',
      cta: 'Inspect rollouts',
    });
  }

  const samples = num(telemetry.samples);
  const haveTelemetry = samples >= THRESHOLDS.minTelemetrySamples;
  if (haveTelemetry
    && telemetry.avgUptimeHours != null
    && Number(telemetry.avgUptimeHours) < THRESHOLDS.lowUptimeHours) {
    items.push({
      id: 'telemetry-uptime',
      severity: 'warning',
      title: `Fleet uptime averages ${formatUptime(telemetry.avgUptimeHours)}`,
      detail: 'Devices are rebooting or reconnecting far more often than expected.',
      to: '/devices',
      cta: 'Inspect devices',
    });
  }

  // --- Informational ------------------------------------------------------
  if (haveTelemetry
    && telemetry.minSignal != null
    && Number(telemetry.minSignal) <= THRESHOLDS.weakSignalDbm) {
    items.push({
      id: 'telemetry-signal',
      severity: 'info',
      title: `Weakest Wi-Fi link at ${Number(telemetry.minSignal)} dBm`,
      detail: 'At least one device is on a marginal connection.',
      to: '/devices',
      cta: 'Check signal',
    });
  }

  const fw = firmwareSummary(devices.firmwareDistribution);
  if (fw.laggingCount > 0) {
    items.push({
      id: 'firmware-lagging',
      severity: 'info',
      title: `${fw.laggingCount} ${plural(fw.laggingCount, 'device')} behind ${fw.latest}`,
      detail: `Still on ${fw.laggingVersions.join(', ')}.`,
      to: '/firmware',
      cta: 'Plan a rollout',
    });
  }

  const unregistered = (devices.provisioning || []).find((p) => p.status === 'unregistered');
  const unregisteredCount = num(unregistered && unregistered.count);
  if (unregisteredCount > 0) {
    items.push({
      id: 'devices-unregistered',
      severity: 'info',
      title: `${unregisteredCount} ${plural(unregisteredCount, 'device')} never finished provisioning`,
      detail: 'Powered up and self-registered, but never claimed by an owner.',
      to: '/devices',
      cta: 'Finish setup',
    });
  }

  // Stable sort: Array#sort preserves insertion order for equal keys, so the
  // push order above is the tiebreak within a severity band.
  return items.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

/** Counts per severity, for the band heading. */
export function triageSummary(items) {
  const counts = { critical: 0, warning: 0, info: 0, total: (items || []).length };
  (items || []).forEach((i) => { counts[i.severity] += 1; });
  return counts;
}
